import { ObjectTools } from '@sol/components/tools/object-tools';
import { SolIcon } from '@sol/icons';
import { SolNavbarItem, SolRouteId, SolRouteIds } from '@sol/components';

export class SolNavbarItemState {
  private constructor(
    public readonly item: SolNavbarItem,
    public readonly activeRouteIds: SolRouteId[],
    public readonly expandedIds: SolRouteId[],
    public readonly level: number,
    public readonly text: string,
    public readonly prependIcon: SolIcon | undefined,
    public readonly appendIcon: SolIcon | undefined,
    public readonly selected: boolean,
    public readonly expanded: boolean,
    public readonly isTopLevelGroup: boolean,
    public readonly isMenuGroup: boolean,
    public readonly children: SolNavbarItem[],
    public readonly buttonClasses: string,
    public readonly isNew: boolean,
    public readonly showBadge: boolean,
  ) {}

  public static of(): SolNavbarItemState {
    return new SolNavbarItemState(
      SolNavbarItem.of(SolRouteIds.error),
      [],
      [],
      0,
      '',
      undefined,
      SolIcon.chevron_down,
      false,
      false,
      false,
      false,
      [],
      '',
      false,
      false,
    ).recalculate();
  }

  public withItem(item: SolNavbarItem): SolNavbarItemState {
    return this.copy({ item }).recalculate();
  }

  public withActiveRouteIds(activeRouteIds: SolRouteId[]): SolNavbarItemState {
    return this.copy({ activeRouteIds }).recalculate();
  }

  public withExpandedIds(expandedIds: SolRouteId[]): SolNavbarItemState {
    return this.copy({ expandedIds }).recalculate();
  }

  public withLevel(level: number): SolNavbarItemState {
    return this.copy({ level }).recalculate();
  }

  private recalculate(): SolNavbarItemState {
    const item = this.item;
    const isTopLevelGroup = item.isMenuGroup && this.level === 0;
    const text = item.displayName || '';
    const selected = this.activeRouteIds.includes(item.id);
    const expanded = this.expandedIds.includes(item.id);
    const children = item.children.filter(child => child.hasAuthority);
    const prependIcon = item.icon;
    const appendIcon = expanded ? SolIcon.chevron_up : SolIcon.chevron_down;
    const isMenuGroup = item.isMenuGroup;
    const isNew = item.isNew;
    const showBadge = item.showBadge;
    return this.copy({
      isMenuGroup,
      isTopLevelGroup,
      text,
      selected,
      children,
      prependIcon,
      appendIcon,
      expanded,
      isNew,
      showBadge,
    }).recalculateClasses();
  }

  private recalculateClasses(): SolNavbarItemState {
    let buttonClasses: string;
    if (this.level < 2) {
      if (this.item.isMenuGroup) {
        buttonClasses = 'lab-navbar-menu-toggle-button';
      } else {
        buttonClasses = 'lab-navbar-menu-button';
      }
    } else {
      buttonClasses = 'lab-navbar-menu-group-button';
    }

    if (this.selected && !this.item.isMenuGroup) {
      buttonClasses += ' lab-navbar-menu-button-selected';
    }
    return this.copy({ buttonClasses });
  }

  private copy(props: Partial<SolNavbarItemState>): SolNavbarItemState {
    return ObjectTools.copy<SolNavbarItemState>(this, props);
  }
}
